import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { brazeUtils } from '../../utils/integrations/braze';
import { IUser } from '../../models';
import { RootState } from '../../store/store';
import { isUserAnonymous } from '../../utils/helpers/user';
import { featureFlag } from '@/utils';
import { GROWTH_BOOK } from '@/constants/features/feature-flag';
import { BrazeAttributeValue } from '@/models/analytics.model';

export const useBraze = () => {
  const user = useSelector((state: RootState) => state.user);
  const isBrazeInitRef = useRef(false);

  // Re-identify user in Braze when user changes (login/logout)
  useEffect(() => {
    if (!isBrazeInitRef.current) return;
    if (!user || isUserAnonymous(user)) return;

    brazeUtils.identifyUserIfNeeded(user);
  }, [user.uid]);

  const init = async (
    initialUser: IUser,
    initWithEmail?: string
  ): Promise<void> => {
    const shouldInitBraze = featureFlag.getFeatureValue<boolean>(
      GROWTH_BOOK.FEATURES.INIT_BRAZE,
      false
    );

    if (!shouldInitBraze) return;

    await brazeUtils.init(initialUser, initWithEmail);
    isBrazeInitRef.current = true;
  };

  const track = (name: string, args?: Record<string, any>) => {
    brazeUtils.sendEvent(name, args);
  };

  const flushData = async () => {
    await brazeUtils.flushData();
  };

  const requestBanners = () => {
    brazeUtils.requestBanners();
  };

  const resetSession = () => {
    brazeUtils.resetSession();
  };

  const openSession = () => {
    brazeUtils.openSession();
  };

  const setCustomAttribute = (name: string, value: BrazeAttributeValue) => {
    brazeUtils.setCustomAttribute(name, value);
  };

  const identifyUserIfNeeded = async (userToIdentify: IUser) => {
    await brazeUtils.identifyUserIfNeeded(userToIdentify);
  };

  return {
    init,
    track,
    flushData,
    requestBanners,
    resetSession,
    openSession,
    setCustomAttribute,
    identifyUserIfNeeded,
  };
};
